// Scrollbars are invisible while idle and only shown during active scrolling
// (see the "Scrollbar visibility" section of styles.css for the paint side).
// This module is the sensing side: one capture-phase `scroll` listener on
// `window` sees every scroll in the document — scroll events don't bubble,
// but they do run the capture phase, so a single listener covers `main`,
// drawers, Radix portals and any future scroll container with no per-component
// wiring. It stamps `data-scrolling` on the element that is scrolling (on
// `<html>` for page-level scroll, whose scrollbar it owns) and removes it
// after a short idle delay.

export const SCROLLING_ATTRIBUTE = 'data-scrolling';

/** Idle delay before a scrollbar fades back out, in ms. */
export const SCROLL_IDLE_MS = 800;

const idleTimers = new WeakMap<Element, ReturnType<typeof setTimeout>>();

/**
 * Scroll events fired by the document scroller target `document`, not
 * `<html>`; the attribute must land on `<html>` because that element owns
 * the page scrollbar.
 */
function resolveScrollTarget(eventTarget: EventTarget | null): Element | null {
  if (eventTarget instanceof Document) return eventTarget.documentElement;
  return eventTarget instanceof Element ? eventTarget : null;
}

function handleScroll(event: Event): void {
  const target = resolveScrollTarget(event.target);
  if (target === null) return;

  target.setAttribute(SCROLLING_ATTRIBUTE, '');

  const pending = idleTimers.get(target);
  if (pending !== undefined) clearTimeout(pending);
  idleTimers.set(
    target,
    setTimeout(() => {
      target.removeAttribute(SCROLLING_ATTRIBUTE);
      idleTimers.delete(target);
    }, SCROLL_IDLE_MS),
  );
}

/**
 * Wire the global listener in. Call once at boot (see main.tsx).
 * Returns an uninstaller — only tests need it.
 */
export function installScrollbarVisibility(win: Window = window): () => void {
  win.addEventListener('scroll', handleScroll, { capture: true, passive: true });
  return () => win.removeEventListener('scroll', handleScroll, { capture: true });
}
